import React, { useState, useEffect } from 'react';
import { Coins, Calculator, ShieldCheck, AlertCircle, Cloud, DollarSign, Heart, X, Phone, Mail, Share2, FileText, Scale, TrendingUp, Clock, ChevronDown, BookOpen, HelpCircle } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot, collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import DonatePage from './DonatePage';
import './App.css';

// --- הגדרות ---
const VAT_RATE = 0.18;
const GRAMS_REQUIRED = 9;

const firebaseConfig = {
  apiKey: "AIzaSyCmzCoamxrqhWVQJ_RzF7dgHiqPHNA6b4E",
  authDomain: "mahtzit-hashekel.firebaseapp.com",
  projectId: "mahtzit-hashekel",
  storageBucket: "mahtzit-hashekel.firebasestorage.app",
  messagingSenderId: "220642800533",
  appId: "1:220642800533:web:4e5219167d39ee3f263c9c",
  measurementId: "G-95B2CDED4G"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// ===== Price History Chart =====
const PriceChart = ({ historyData }) => {
  if (!historyData || historyData.length < 2) {
    return (
      <div className="chart-empty">
        <TrendingUp size={24} />
        <span>הנתונים ההיסטוריים יופיעו כאן לאחר מספר עדכונים</span>
      </div>
    );
  }

  const W = 800, H = 300;
  const PAD = { top: 30, right: 20, bottom: 50, left: 70 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const prices = historyData.map(d => d.priceILS);
  const times = historyData.map(d => d.timestamp);
  const minP = Math.min(...prices) * 0.998;
  const maxP = Math.max(...prices) * 1.002;
  const rangeP = maxP - minP || 1;
  const minT = times[0];
  const maxT = times[times.length - 1];
  const rangeT = maxT - minT || 1;

  const toX = (t) => PAD.left + ((t - minT) / rangeT) * chartW;
  const toY = (p) => PAD.top + chartH - ((p - minP) / rangeP) * chartH;

  // Build path
  const points = historyData.map((d, i) => `${toX(d.timestamp)},${toY(d.priceILS)}`);
  const linePath = `M${points.join(' L')}`;
  const areaPath = `${linePath} L${toX(maxT)},${PAD.top + chartH} L${toX(minT)},${PAD.top + chartH} Z`;

  // Grid lines (5 horizontal)
  const gridLines = [];
  for (let i = 0; i <= 4; i++) {
    const val = minP + (rangeP * i) / 4;
    const y = toY(val);
    gridLines.push({ y, label: val.toFixed(2) });
  }

  // Date labels - pick up to 5 evenly spaced
  const dateLabels = [];
  const step = Math.max(1, Math.floor(historyData.length / 5));
  for (let i = 0; i < historyData.length; i += step) {
    const d = new Date(historyData[i].timestamp);
    const label = `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    dateLabels.push({ x: toX(historyData[i].timestamp), label });
  }

  // Current price marker (last point)
  const lastD = historyData[historyData.length - 1];
  const lastX = toX(lastD.timestamp);
  const lastY = toY(lastD.priceILS);

  return (
    <div className="chart-container">
      <svg viewBox={`0 0 ${W} ${H}`} className="price-chart-svg">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent-light)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--color-accent-light)" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--color-accent)" />
            <stop offset="100%" stopColor="var(--color-gold)" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={g.y} x2={W - PAD.right} y2={g.y} stroke="var(--color-border)" strokeWidth="0.5" strokeDasharray="4,4" />
            <text x={PAD.left - 8} y={g.y + 4} textAnchor="end" fill="var(--color-text-muted)" fontSize="11" fontFamily="var(--font-family)">₪{g.label}</text>
          </g>
        ))}

        {/* Date labels */}
        {dateLabels.map((dl, i) => (
          <text key={i} x={dl.x} y={H - 8} textAnchor="middle" fill="var(--color-text-muted)" fontSize="10" fontFamily="var(--font-family)">{dl.label}</text>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill="url(#areaGrad)" />

        {/* Line */}
        <path d={linePath} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Current price dot */}
        <circle cx={lastX} cy={lastY} r="5" fill="var(--color-gold)" stroke="var(--color-bg)" strokeWidth="2" />
        <circle cx={lastX} cy={lastY} r="8" fill="var(--color-gold)" opacity="0.3">
          <animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
};

const ContactPopup = ({ onClose }) => (
  <div className="popup-overlay" onClick={onClose}>
    <div className="popup-content" onClick={e => e.stopPropagation()}>
      <button className="popup-close" onClick={onClose}>
        <X size={20} />
      </button>
      <h3 className="popup-title">יצירת קשר</h3>
      <div className="contact-list">

        <a href="tel:0583730000" className="contact-item">
          <Phone size={18} className="contact-icon" />
          <span>058-3730000</span>
        </a>
        <a href="https://wa.me/972583730000" target="_blank" rel="noopener noreferrer" className="contact-item">
          <svg className="contact-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
          <span>058-3730000</span>
        </a>
        <a href="mailto:yairdahn@gmail.com" className="contact-item">
          <Mail size={18} className="contact-icon" />
          <span>yairdahn@gmail.com</span>
        </a>
      </div>
    </div>
  </div>
);

// ===== Privacy Policy Popup =====
const PrivacyPopup = ({ onClose }) => (
  <div className="popup-overlay" onClick={onClose}>
    <div className="popup-content legal-popup" onClick={e => e.stopPropagation()}>
      <button className="popup-close" onClick={onClose}>
        <X size={20} />
      </button>
      <h3 className="popup-title">מדיניות פרטיות</h3>
      <div className="legal-text">
        <p><strong>עדכון אחרון:</strong> פברואר 2026</p>

        <h4>1. כללי</h4>
        <p>אתר "זכר למחצית השקל" ("האתר") מכבד את פרטיותך. מדיניות פרטיות זו מסבירה אילו נתונים נאספים, כיצד הם משמשים, ומהן זכויותיך בנוגע למידע.</p>

        <h4>2. מידע שנאסף</h4>
        <p>האתר משתמש ב-<strong>Google Analytics</strong> לצורך ניתוח סטטיסטי בלבד. המידע הנאסף כולל:</p>
        <ul>
          <li>כתובת IP (באופן אנונימי)</li>
          <li>סוג דפדפן ומערכת הפעלה</li>
          <li>דפים שנצפו ומשך הגלישה</li>
          <li>מקור ההגעה לאתר</li>
        </ul>
        <p>איננו אוספים מידע אישי מזהה כגון שם, כתובת מייל, או מספר טלפון באמצעות כלי הניתוח.</p>

        <h4>3. קובצי עוגיות (Cookies)</h4>
        <p>האתר משתמש בקובצי עוגיות של Google Analytics לצורך ניתוח תעבורת הגולשים. ניתן לחסום עוגיות באמצעות הגדרות הדפדפן שלך, אך ייתכן שחלק מהפונקציונליות של האתר תושפע.</p>

        <h4>4. שיתוף מידע עם צדדים שלישיים</h4>
        <p>המידע הסטטיסטי נאסף ומעובד על ידי Google Analytics בלבד. איננו מוכרים, משכירים, או מעבירים מידע אישי לצדדים שלישיים.</p>

        <h4>5. אבטחת מידע</h4>
        <p>האתר מאוחסן על שרתי Google Cloud (Firebase) ומשתמש בפרוטוקול HTTPS מוצפן להגנה על התעבורה.</p>

        <h4>6. זכויותיך</h4>
        <p>באפשרותך לבקש מאיתנו למחוק כל מידע שנאסף אודותיך, או להתנגד לאיסוף נתונים. ניתן לפנות אלינו באמצעות פרטי הקשר באתר.</p>

        <h4>7. שינויים במדיניות</h4>
        <p>אנו שומרים לעצמנו את הזכות לעדכן מדיניות זו מעת לעת. השימוש המתמשך באתר מהווה הסכמה למדיניות המעודכנת.</p>
      </div>
    </div>
  </div>
);

// ===== Terms of Use Popup =====
const TermsPopup = ({ onClose }) => (
  <div className="popup-overlay" onClick={onClose}>
    <div className="popup-content legal-popup" onClick={e => e.stopPropagation()}>
      <button className="popup-close" onClick={onClose}>
        <X size={20} />
      </button>
      <h3 className="popup-title">תנאי שימוש</h3>
      <div className="legal-text">
        <p><strong>עדכון אחרון:</strong> פברואר 2026</p>

        <h4>1. כללי</h4>
        <p>השימוש באתר "זכר למחצית השקל" ("האתר") כפוף לתנאי שימוש אלה. גלישה באתר מהווה הסכמה מלאה ובלתי מותנית לכל התנאים המפורטים להלן.</p>

        <h4>2. הגבלת אחריות — "כפי שהוא" (As-Is)</h4>
        <p>המידע המוצג באתר, לרבות מחירי כסף, שערי חליפין, וחישובי ערך מחצית השקל, מוצג <strong>"כפי שהוא" (As-Is)</strong> וללא כל אחריות מכל סוג שהוא, במפורש או במשתמע.</p>

        <h4>3. דיוק הנתונים</h4>
        <p>הנתונים המוצגים באתר נשאבים ממקורות חיצוניים (בורסות מתכות ושערי חליפין) ומתעדכנים מספר פעמים ביום. עם זאת:</p>
        <ul>
          <li>הנתונים <strong>עלולים להיות מעוכבים</strong> ביחס למחיר הנוכחי בבורסה בשל תדירות הדגימה של השרת.</li>
          <li>הנתונים <strong>אינם בהכרח מדויקים ב-100%</strong> ועלולים לכלול סטיות מהמחיר בפועל.</li>
          <li>הנהלת האתר <strong>אינה אחראית</strong> לכל אי-דיוק, שגיאה, השמטה, או עיכוב בנתונים המוצגים.</li>
        </ul>

        <h4>4. שימוש על אחריות המשתמש</h4>
        <p>השימוש בנתונים המוצגים באתר הוא <strong>על אחריות המשתמש בלבד</strong>. הנהלת האתר לא תישא בשום אחריות לכל נזק ישיר, עקיף, תוצאתי, או כל נזק אחר הנובע מהשימוש במידע המוצג באתר.</p>

        <h4>5. אין ייעוץ פיננסי או מסחרי</h4>
        <p>המידע באתר <strong>אינו מהווה ייעוץ פיננסי, מסחרי, השקעתי, או כל ייעוץ מקצועי אחר</strong>. אין להסתמך על הנתונים לצורך קבלת החלטות כלכליות או מסחריות. לצורך כך, יש להיוועץ במומחה מוסמך.</p>

        <h4>6. מטרת האתר</h4>
        <p>האתר נועד לסייע לציבור בחישוב ערך "זכר למחצית השקל" <strong>לצורך מידע בלבד</strong>, כמנהג ישראל לתת זכר למחצית השקל בערב פורים.</p>

        <h4>7. קניין רוחני</h4>
        <p>כל התכנים, העיצוב, והקוד של האתר מוגנים בזכויות יוצרים. אין להעתיק, לשכפל, או להפיץ חלקים מהאתר ללא אישור מראש.</p>

        <h4>8. שינויים</h4>
        <p>הנהלת האתר שומרת לעצמה את הזכות לשנות תנאים אלו בכל עת וללא הודעה מוקדמת. המשך השימוש באתר לאחר שינוי התנאים מהווה הסכמה לתנאים החדשים.</p>

        <h4>9. דין חל</h4>
        <p>על תנאי שימוש אלו יחולו דיני מדינת ישראל בלבד. סמכות השיפוט הבלעדית נתונה לבתי המשפט המוסמכים בישראל.</p>
      </div>
    </div>
  </div>
);

const App = () => {
  const [data, setData] = useState({
    priceILS: 0,
    priceUSD: 0,
    lastUpdated: null,
    source: 'loading'
  });
  const [loading, setLoading] = useState(true);
  const [showVat, setShowVat] = useState(true);
  const [gramsOption, setGramsOption] = useState(GRAMS_REQUIRED); // 9 or 9.6
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState('home'); // 'home' | 'donate'
  const [donateInitialType, setDonateInitialType] = useState(null); // for cross-sell
  const [showContact, setShowContact] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  // האזנה ל-Firestore בלבד — Cloud Function מעדכנת כל 15 דקות
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'silver_prices', 'rates'),
      (docSnap) => {
        if (docSnap.exists()) {
          const d = docSnap.data();
          setData({
            priceILS: Number(d.priceILS) || 0,
            priceUSD: Number(d.priceUSD) || 0,
            lastUpdated: d.timestamp,
            source: d.source || 'Unknown'
          });
          setLoading(false);
        } else {
          setError('הנתונים טרם עודכנו — נסה שוב בעוד מספר דקות');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Firestore error:', err);
        setError('שגיאה בחיבור למסד הנתונים');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // שליפת היסטוריית מחירים
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const histRef = collection(db, 'silver_prices', 'rates', 'history');
        const q = query(histRef, orderBy('timestamp', 'asc'), limit(700));
        const snapshot = await getDocs(q);
        const points = snapshot.docs.map(d => d.data());
        setHistoryData(points);
      } catch (err) {
        console.error('Failed to load history:', err);
      }
    };
    fetchHistory();
  }, [data.lastUpdated]); // refetch when data updates

  // Dynamic SEO title with real-time price
  useEffect(() => {
    if (totalWithVat > 0) {
      document.title = `מחצית השקל ${new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(totalWithVat)} — זכר למחצית השקל תשפ״ו | מחיר עדכני`;
    } else {
      document.title = 'זכר למחצית השקל תשפ״ו — שווי מחצית השקל בזמן אמת | מחיר כסף עדכני 2026';
    }
  });

  // FAQ items for SEO + user value
  const faqItems = [
    {
      q: 'כמה שווה מחצית השקל?',
      a: 'שווי מחצית השקל נקבע לפי ערך 9 גרם כסף טהור. הסכום משתנה בהתאם למחיר הכסף בשוק העולמי. לפורים תשפ״ו (2026), הסכום עומד על כ-80-86 ₪ כולל מע״מ, בהתאם לפסיקות הרבנים השונים. באתר זה תוכלו לקבל את המחיר המעודכן ביותר בזמן אמת.'
    },
    {
      q: 'כמה גרם כסף במחצית השקל?',
      a: 'שיעור מחצית השקל הוא 3 דרהם, ונחלקו הפוסקים כמה זה בגרמים. לפי רוב הפוסקים — 9 גרם כסף טהור. לפי שיטות אחרות (כגון מנהג חב"ד) — 9.6 גרם. באתר זה ניתן לבחור בין שתי השיטות: ברירת המחדל היא 9 גרם, ויש אפשרות לעבור לחישוב לפי 9.6 גרם.'
    },
    {
      q: 'האם צריך לתת מחצית השקל כולל מע״מ?',
      a: 'יש מחלוקת בין הפוסקים. חלק מהרבנים פוסקים שזכר למחצית השקל ניתן ללא מע"מ (ערך הכסף הגולמי), וחלק פוסקים כולל מע"מ (18%). האתר מציג את שני החישובים כדי שכל אחד יוכל לתת כמנהגו.'
    },
    {
      q: 'עבור מי נותנים מחצית השקל?',
      a: 'מרבית הפוסקים סוברים שנותנים עבור כל גבר מגיל 20 ומעלה. רבים נוהגים לתת גם עבור בני הבית: נשים, ילדים קטנים, ויש הנוהגים גם עבור עוברים. מנהג ספרדים לתת גם לנשים וילדים, ואילו אצל אשכנזים המנהג משתנה.'
    },
    {
      q: 'מתי נותנים זכר למחצית השקל?',
      a: 'המנהג המקובל הוא לתת זכר למחצית השקל בתענית אסתר, לפני תפילת מנחה. יש הנוהגים לתת כבר מראש חודש אדר. הכסף ניתן לצדקה — לתמיכה בלומדי תורה, כוללים ומשפחות נזקקות.'
    },
    {
      q: 'מה ההבדל בין מחצית השקל למתנות לאביונים?',
      a: 'זכר למחצית השקל הוא מנהג לתת את שווי 9 גרם כסף כזכר למצוות מחצית השקל שניתנה בזמן בית המקדש. מתנות לאביונים היא מצווה מדרבנן לתת לפחות לשני עניים ביום הפורים עצמו — זוהי מצוות חובה של פורים.'
    }
  ];

  // חישובים לתצוגה
  const baseValue = data.priceILS * gramsOption;
  const vatAmount = baseValue * VAT_RATE;
  const totalWithVat = baseValue + vatAmount;
  const baseValueUSD = data.priceUSD * gramsOption;

  const formatCurrency = (amount, currency = 'ILS') => {
    if (amount === undefined || amount === null || isNaN(amount) || amount === 0) return '---';
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getTimeAgo = () => {
    if (!data.lastUpdated) return '';
    const diff = Date.now() - data.lastUpdated;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `לפני ${hours} שעות ו-${minutes} דקות`;
    return `לפני ${minutes} דקות`;
  };

  // חישוב זמן העדכון הבא (כל 15 דקות, חוץ מלילה ושבת)
  const getNextUpdateTime = () => {
    const now = new Date();
    // Round up to next 15-min mark
    let next = new Date(now);
    next.setSeconds(0, 0);
    const mins = next.getMinutes();
    const remainder = mins % 15;
    next.setMinutes(mins + (15 - remainder));

    // Check up to 48 iterations (12 hours max) to find next active slot
    for (let i = 0; i < 48; i++) {
      const h = next.getHours();
      const d = next.getDay(); // 0=Sun, 5=Fri, 6=Sat

      const isNight = h < 7;
      const isShabbatFri = d === 5 && h >= 17;
      const isShabbatSat = d === 6 && h < 19;

      if (!isNight && !isShabbatFri && !isShabbatSat) {
        return next;
      }
      next = new Date(next.getTime() + 15 * 60 * 1000);
    }
    return null;
  };

  const formatNextUpdate = () => {
    const next = getNextUpdateTime();
    if (!next) return '';
    return `${String(next.getHours()).padStart(2, '0')}:${String(next.getMinutes()).padStart(2, '0')}`;
  };

  const sourceLabel = () => {
    if (data.source === 'loading') return 'טוען...';
    if (data.source.includes('metals.dev')) return 'metals.dev (בורסה)';
    if (data.source.includes('Coinbase')) return 'Coinbase (גיבוי)';
    return data.source;
  };

  // Primary / Secondary prices based on toggle
  const primaryPrice = showVat ? totalWithVat : baseValue;
  const secondaryPrice = showVat ? baseValue : totalWithVat;
  const primaryLabel = showVat ? 'כולל מע"מ (18%)' : 'ללא מע"מ';
  const secondaryLabel = showVat ? 'ללא מע"מ' : 'כולל מע"מ (18%)';

  const navigateToDonate = (initialType = null) => {
    setDonateInitialType(initialType);
    setCurrentPage('donate');
  };

  const navigateHome = () => {
    setCurrentPage('home');
    setDonateInitialType(null);
  };

  // Share functionality
  const handleShare = async () => {
    const shareData = {
      title: 'זכר למחצית השקל — ערך מחצית השקל בזמן אמת',
      text: `שווי מחצית השקל (${gramsOption} גרם כסף) עומד כעת על ${formatCurrency(totalWithVat)} כולל מע"מ. בדקו את המחיר העדכני:`,
      url: 'https://mahtzit-hashekel.web.app'
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    } else {
      // Fallback: copy to clipboard
      const text = `${shareData.text}\n${shareData.url}`;
      try {
        await navigator.clipboard.writeText(text);
        alert('הקישור הועתק!');
      } catch {
        // Final fallback: open WhatsApp
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }
    }
  };

  // ===== Donation Page =====
  if (currentPage === 'donate') {
    return (
      <DonatePage
        silverPrice={data.priceILS}
        onBack={navigateHome}
        initialType={donateInitialType}
        defaultGrams={gramsOption}
      />
    );
  }

  return (
    <div className="app" dir="rtl">
      {/* ===== Header ===== */}
      <header className="header">
        <div className="header-inner">
          <div className="header-brand">
            <div className="header-icon" style={{ background: 'transparent', boxShadow: 'none' }}>
              <img src="/logo.png" alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div>
              <h1 className="header-title">זכר למחצית השקל</h1>
              <div className="header-subtitle">
                <Cloud size={12} />
                <span>מחובר לענן (Google Cloud)</span>
              </div>
            </div>
          </div>
          <div className="header-live-badge">
            <span className="header-live-dot"></span>
            <span>נתונים חיים</span>
          </div>
        </div>
      </header>

      {/* ===== Main ===== */}
      <main className="main-content">

        {/* Error */}
        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{String(error)}</span>
          </div>
        )}

        {/* ===== Hero Card ===== */}
        <div className="hero-card">
          <h2 className="hero-label">
            שווי {gramsOption} גרם כסף טהור
            <span className="hero-sublabel">(זכר למחצית השקל)</span>
          </h2>

          {/* Grams selector */}
          <div className="grams-selector">
            <button
              className={`grams-option ${gramsOption === 9 ? 'grams-option--active' : ''}`}
              onClick={() => setGramsOption(9)}
            >
              9 <br /> גרם
              {/* <span className="grams-option-desc">רוב הפוסקים</span> */}
            </button>
            <button
              className={`grams-option ${gramsOption === 9.6 ? 'grams-option--active' : ''}`}
              onClick={() => setGramsOption(9.6)}
            >
              9.6<br /> גרם
              {/* <span className="grams-option-desc">שיטת חב״ד</span> */}
            </button>
          </div>

          <div className="price-display">
            {loading ? (
              <div className="price-loading"></div>
            ) : (
              <>
                {/* Main price */}
                <div className="price-main" key={showVat ? 'vat' : 'noVat'}>
                  {formatCurrency(primaryPrice)}
                </div>
                <span className="price-vat-label">{primaryLabel}</span>

                {/* Secondary price — always visible */}
                <div className="price-secondary">
                  <span>{secondaryLabel}:</span>
                  <span className="price-secondary-amount">
                    {formatCurrency(secondaryPrice)}
                  </span>
                </div>

                {/* USD price */}
                <div className="price-usd">
                  <DollarSign size={14} />
                  <span>{formatCurrency(baseValueUSD, 'USD')} (ללא מע"מ)</span>
                </div>
              </>
            )}
          </div>

          {/* Source & Update Status */}
          <div className="status-area">
            <div className="source-badge">
              <span className={`source-dot ${data.source.includes('metals') ? 'source-dot--metals' : 'source-dot--other'}`}></span>
              <span>מקור: {sourceLabel()}</span>
            </div>
            {!loading && (
              <div className="update-times">
                <span className="update-time">
                  <Clock size={11} />
                  עודכן {getTimeAgo()}
                </span>
                {formatNextUpdate() && (
                  <span className="update-time update-time--next">
                    עדכון הבא: {formatNextUpdate()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== Donate Button ===== */}
        <div className="donate-section">
          <button className="donate-btn" onClick={() => navigateToDonate()}>
            <Heart size={20} />
            תרום לזכר למחצית השקל
          </button>
        </div>

        {/* ===== Share Button ===== */}
        <div className="share-section">
          <button className="share-btn" onClick={handleShare}>
            <Share2 size={16} />
            שתפו את האתר
          </button>
        </div>

        {/* ===== Price History Chart ===== */}
        <div className="card chart-card">
          <h3 className="card-title">
            <TrendingUp size={18} />
            היסטוריית מחיר גרם כסף (₪)
          </h3>
          <PriceChart historyData={historyData} />
        </div>

        {/* ===== Cards Grid ===== */}
        <div className="cards-grid">
          {/* Calculation Breakdown */}
          <div className="card">
            <h3 className="card-title">
              <Calculator size={18} />
              תחשיב מדויק
            </h3>
            <div className="calc-rows">
              <div className="calc-row">
                <span className="calc-label">מחיר גרם כסף</span>
                <span className="calc-value">{formatCurrency(data.priceILS)}</span>
              </div>
              <div className="calc-row">
                <span className="calc-label">כמות ({gramsOption} גרם)</span>
                <span className="calc-value">{formatCurrency(baseValue)}</span>
              </div>
              <div className="calc-row">
                <span className="calc-label">מע"מ (18%)</span>
                <span className="calc-value">+{formatCurrency(vatAmount)}</span>
              </div>
              <div className="calc-row calc-row--total">
                <span className="calc-label">סה"כ לתשלום</span>
                <span className="calc-value">{formatCurrency(totalWithVat)}</span>
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div className="card card--info">
            <Coins className="info-coin-decoration" size={120} />
            <h3 className="card-title">
              <ShieldCheck size={18} />
              הסבר על עידכון הנתונים באתר
            </h3>
            <p className="info-text">
              האתר מחובר למסד נתונים מרכזי בענן. הנתונים מתעדכנים אוטומטית כל 15 דקות (בשעות הפעילות).
              <span className="info-note">
                * הנהלת האתר אינה אחראית על אמינות הנתונים, ונועד לצורך מידע בלבד.
              </span>
            </p>
          </div>
        </div>

        {/* ===== SEO-Rich FAQ Section ===== */}
        <article className="seo-section">
          <h2 className="seo-section-title">
            <BookOpen size={20} />
            מידע על זכר למחצית השקל — פורים תשפ״ו
          </h2>
          <p className="seo-intro">
            זכר למחצית השקל הוא מנהג ישראל מקדמא דנא, לתת לצדקה את שווי מחצית השקל
            — כפי שהיו נותנים בזמן בית המקדש בחודש אדר. כיום, השווי מחושב לפי 9 גרם כסף טהור
            במחיר השוק העולמי. אתר זה מעדכן את המחיר אוטומטית כל 15 דקות בשעות הפעילות,
            ומאפשר לציבור לדעת בדיוק כמה לתת — ואף לתרום ישירות באופן מקוון.
          </p>

          <section className="faq-section">
            <h3 className="faq-title">
              <HelpCircle size={18} />
              שאלות נפוצות על מחצית השקל
            </h3>
            <div className="faq-list">
              {faqItems.map((item, idx) => (
                <div
                  key={idx}
                  className={`faq-item ${openFaq === idx ? 'faq-item--open' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    aria-expanded={openFaq === idx}
                  >
                    <span>{item.q}</span>
                    <ChevronDown size={16} className="faq-chevron" />
                  </button>
                  <div className="faq-answer">
                    <p>{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </article>

        {/* ===== Donate Button ===== */}
        <div className="donate-section">
          <button className="donate-btn" onClick={() => navigateToDonate()}>
            <Heart size={20} />
            תרום לזכר למחצית השקל
          </button>
        </div>

      </main>

      {/* ===== Footer ===== */}
      <footer className="footer">
        <p>נתונים מ-metals.dev &amp; Coinbase</p>
        <div className="footer-links">
          <button className="footer-link" onClick={() => setShowPrivacy(true)}>
            <FileText size={12} />
            מדיניות פרטיות
          </button>
          <span className="footer-divider">|</span>
          <button className="footer-link" onClick={() => setShowTerms(true)}>
            <Scale size={12} />
            תנאי שימוש
          </button>
        </div>
        <div className="footer-credit" onClick={() => setShowContact(true)}>
          נבנה ע"י y.d. systems
        </div>
      </footer>

      {showContact && <ContactPopup onClose={() => setShowContact(false)} />}
      {showPrivacy && <PrivacyPopup onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsPopup onClose={() => setShowTerms(false)} />}
    </div>
  );
};

export default App;